import {
  FinanceQuote,
  FinanceQuoteIrregularPaymentStream,
  GenericGrid,
  getOptionsFromEnumAndLoanClass, IrregularPaymentType, PaymentHandling
} from '@amsanalytics/react-shared-library';
import styles from './_finance-irregular-payment-stream-editor.module.scss';
import {v4} from "uuid";
import produce from "immer";
import {Card, CardHeader, CardTitle} from '@progress/kendo-react-layout';
import AddIcon from "@mui/icons-material/Add";
import {IconButton} from '@mui/material';
import {ILookupItems} from "../../../../Interfaces/ILookupItems";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import FinanceIrregularPaymentStreamEditorDialog
  from "./FinanceIrregularPaymentStreamEditorDialog/FinanceIrregularPaymentStreamEditorDialog";
import { maxPaymentStreams } from '../../../../utils/quoteUtils';
import {getFinanceQuoteAmountFinanced} from "../../../../utils/LoanCalcUtils";

interface FinanceQuoteIrregularPaymentStreamWithDescription extends FinanceQuoteIrregularPaymentStream {
  paymentFrequencyDescription: string;
}

export interface FinanceIrregularPaymentStreamEditorProps {
  selectedFinanceQuote: FinanceQuote;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  lookups: ILookupItems;
  currentApp: IApplication;
  className?: string;
  openDialog: (options: { children: React.ReactNode }) => void;
  closeDialog: () => void;
  onUpdateApp: (updatedApp: IApplication) => void;
}

export default function FinanceIrregularPaymentEditor(props: FinanceIrregularPaymentStreamEditorProps) {
  const { openDialog, closeDialog } = props;
  const irregularPayments = [...props.selectedFinanceQuote.financeQuoteIrregularPaymentStreams]
    .sort((a, b) =>
      new Date(a.startDate!) > new Date(b.startDate!) ? 1 : -1
    );
  const paymentFrequencies = getOptionsFromEnumAndLoanClass(props.lookups.carletonItems, "PaymentFrequency", props.selectedFinanceQuote.financeLoanClassId);

  const handleRemoveSingleIrregularPayment = (irregularPaymentId: string) => {
    // Using this to match on the date for the other quotes
    const irregularPaymentToRemoveFromSelectedQuote =
      props.selectedFinanceQuote.financeQuoteIrregularPaymentStreams.find(dp => dp.id === irregularPaymentId);

    if (!irregularPaymentToRemoveFromSelectedQuote) {
      return;
    }

    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      // Applying the same values to each quote
      updatedCurrentApp.financeQuotes.forEach((financeQuote: FinanceQuote) => {
        // Taking out the irregular payment for the date of the one that was removed on the selected quote
        financeQuote.financeQuoteIrregularPaymentStreams = financeQuote.financeQuoteIrregularPaymentStreams
          .filter(payment => payment.id !== irregularPaymentToRemoveFromSelectedQuote.id);
      });
    });
    props.onUpdateApp(updatedApp);

    closeDialog();
  }

  const handleUpdateIrregularPayment = (irregularPaymentStream: FinanceQuoteIrregularPaymentStream): void => {    
    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      // Applying the same values to each quote
      updatedCurrentApp.financeQuotes.forEach((financeQuoteToUpdate: FinanceQuote) => {
        // Removing the old one, if exists, and adding the updated one
        financeQuoteToUpdate.financeQuoteIrregularPaymentStreams = financeQuoteToUpdate.financeQuoteIrregularPaymentStreams
          .filter(ips => ips.id !== irregularPaymentStream.id);
        // Adding the updated one
        financeQuoteToUpdate.financeQuoteIrregularPaymentStreams.push(irregularPaymentStream);
      });
    });
    props.onUpdateApp(updatedApp);
    closeDialog();
  }
  
  const addOrEditIrregularPayment = (irregularPaymentStream: FinanceQuoteIrregularPaymentStream | undefined) => {
    const buttonText = irregularPaymentStream ? "Update" : "Add";
    const showRemoveButton = !!irregularPaymentStream;
    if (!irregularPaymentStream) {
      irregularPaymentStream = {
        id: v4(), // Have to have an ID so that it can be updated, if needed
        financeQuoteId: props.selectedFinanceQuote.id,
        startDate: null,
        endDate: null,
        paymentFrequency: props.selectedFinanceQuote.paymentFrequency,
        amount: 0,
        count: 1,
        type: IrregularPaymentType.Range,
        handling: PaymentHandling.PrincipalAndInterest
      };
    }
    
    const amountToFinance = getFinanceQuoteAmountFinanced(props.currentApp, props.lookups.financeLoanClasses);

    openDialog({
      children: (
        <FinanceIrregularPaymentStreamEditorDialog
          title={"Irregular Payment Stream"}
          irregularPaymentStream={irregularPaymentStream}
          submitButtonText={buttonText}
          onSubmit={(irregularPaymentStream: FinanceQuoteIrregularPaymentStream) => handleUpdateIrregularPayment(irregularPaymentStream)}
          closeDialog={closeDialog}
          onRemoveSingle={handleRemoveSingleIrregularPayment}
          selectedFinanceQuote={props.selectedFinanceQuote}
          lookups={props.lookups}
          maxNumberOfPaymentStreams={maxPaymentStreams}
          theoreticalAmountToFinance={amountToFinance}
        />
      )
    })
  }
  
  return (
    <div className={styles.financeIrregularPaymentEditorContainer}>
      <Card className={props.className}>
        <CardHeader>
          <CardTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '17.5px' }}>
              <span>Irregular Payments (Input)</span>
              {!(props.financeQuoteFieldIsDisabled("irregularPayments")) && (
                <IconButton
                  size='small'
                  title={'Add Irregular Payment Stream'}
                  style={{ color: '#757575' }}
                  onClick={() => addOrEditIrregularPayment(undefined)}
                >
                  <AddIcon fontSize='small' />
                </IconButton>
              )}

            </div>
          </CardTitle>
        </CardHeader>
        <div className={styles.sectionItem}>
          <GenericGrid<FinanceQuoteIrregularPaymentStreamWithDescription>
            size={'small'}
            useIconButtons={true}
            columns={[
              { title: "Start Date", field: "startDate", width: 125, format: "{0:d}" },
              { title: "End Date", field: "endDate", width: 125, format: "{0:d}" },
              { title: "Count", field: "count", width: 80 },
              { title: "Payment", field: "amount", width: 100, format: "{0:c}" }
            ]}
            items={irregularPayments.map(payment => { 
              return { 
                ...payment, 
                startDate: payment.startDate ? new Date(payment.startDate) : null,
                endDate: payment.endDate ? new Date(payment.endDate) : null,
                paymentFrequencyDescription: paymentFrequencies.find(pf => pf.value === payment.paymentFrequency)?.option ?? "Unknown"
              } 
            })}
            disableAdding={true}
            disableEditing={props.financeQuoteFieldIsDisabled("irregularPayments") || irregularPayments.length > maxPaymentStreams}
            disableExcelExporting={true}
            disablePdfExporting={true}
            disableFiltering={true}
            disableGrouping={true}
            onOpenItemEditor={(irregularPayment: FinanceQuoteIrregularPaymentStream | undefined) => addOrEditIrregularPayment(irregularPayment)}
            onItemSelected={(irregularPayment: FinanceQuoteIrregularPaymentStream) => addOrEditIrregularPayment(irregularPayment)}
            disablePaging={irregularPayments.length <= 20}
          />
        </div>
      </Card>
    </div>
  );
}
